import { useEffect, useState, type CSSProperties } from "react";
import { E32 } from "../theme/endesga32";

// Sprite path resolution. By convention, an item's sprite lives at
// `/items/<id>.png` (e.g. `/items/ancient_skull_key.png`). The
// `RENAMES` map only carries ids whose filename doesn't match the id
// — booster pack art shared across factions, sprite shorthand keys
// like `pack` / `deckbox`, and the legacy red-apple / red-strawberry
// PNGs (the item id is the generic "apple" / "strawberry" but the
// file is named for the ripe colour). New items don't need an entry
// here at all; just drop `<id>.png` under `frontend/public/items/`.
const RENAMES: Record<string, string> = {
  apple: "/items/apple_red.png",
  strawberry: "/items/strawberry_red.png",
  high_fantasia_pack: "/items/booster_pack_high_fantasia.png",
  dwarf_booster_pack: "/items/booster_pack_high_fantasia_dwarf.png",
  elf_booster_pack: "/items/booster_pack_high_fantasia_elf.png",
  goblin_booster_pack: "/items/booster_pack_high_fantasia_goblin.png",
  human_booster_pack: "/items/booster_pack_high_fantasia_human.png",
  orc_booster_pack: "/items/booster_pack_high_fantasia_orc.png",
  pack: "/items/booster_pack_high_fantasia.png",
  deckbox: "/items/deck_box.png",
};

const COLORS: Record<string, string> = {
  apple: E32.ruby,
  strawberry: E32.scarlet,
  mushroom: E32.brick,
  pack: E32.amber,
  deckbox: E32.oxblood,
  juice_potion: E32.mulberry,
  high_fantasia_pack: E32.amber,
  deck_box: E32.oxblood,
};

const FALLBACK = E32.mist;

/**
 * Display scale for a `w×h` source sprite inside a `box×box` tile.
 *
 * When the source fits (`box ≥ source`), snap to the largest whole
 * number of pixels-per-pixel that still fits — crisp pixel-art upscale
 * with no fractional "scrunch". When the source is larger than the box
 * (a small consumer, e.g. a 48 px reward row showing 64 px art), fall
 * back to the exact fit factor so it shrinks to fit rather than being
 * cropped.
 */
export function pixelScale(box: number, w: number, h: number): number {
  const fit = Math.min(box / w, box / h);
  return fit >= 1 ? Math.floor(fit) : fit;
}

interface Props {
  id: string;
  size?: number;
}

export function ItemSprite({ id, size = 48 }: Props) {
  const src = RENAMES[id] ?? `/items/${id}.png`;
  // `failed` flips to true when the convention-path 404s and there's
  // no rename entry; we then fall back to the colored-circle
  // placeholder. Reset whenever id changes so re-renders with a new
  // id retry the image.
  const [failed, setFailed] = useState(false);
  // Natural (source) pixel dimensions, captured on load, so the image
  // can be scaled by a whole-number factor rather than fitted
  // fractionally.
  const [nat, setNat] = useState<{ w: number; h: number } | null>(null);
  useEffect(() => {
    setFailed(false);
    setNat(null);
  }, [id]);

  if (!failed) {
    // Pixel-perfect scaling: render the source at the largest integer
    // multiple that still fits the fixed `size × size` box, centered.
    // Integer scaling keeps pixel art crisp — no fractional "scrunch"
    // from `object-fit: contain` — and the box stays fixed regardless
    // of the art's authored dimensions. Hidden until the natural size
    // is known so it can't flash at 1×.
    const imgStyle: CSSProperties = nat
      ? (() => {
          const scale = pixelScale(size, nat.w, nat.h);
          return {
            width: nat.w * scale,
            height: nat.h * scale,
            imageRendering: "pixelated",
          };
        })()
      : { imageRendering: "pixelated", opacity: 0 };

    return (
      <div
        style={{
          width: size,
          height: size,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          overflow: "hidden",
          flexShrink: 0,
        }}
      >
        <img
          src={src}
          alt=""
          aria-hidden="true"
          onLoad={(e) =>
            setNat({
              w: e.currentTarget.naturalWidth,
              h: e.currentTarget.naturalHeight,
            })
          }
          onError={() => setFailed(true)}
          style={imgStyle}
        />
      </div>
    );
  }

  const color = COLORS[id] ?? FALLBACK;
  return (
    <div
      aria-hidden="true"
      style={{
        width: size,
        height: size,
        borderRadius: "50%",
        background: color,
        border: `2px solid ${E32.ink}`,
        boxShadow:
          "inset 2px 2px 0 rgba(255,255,255,0.25), inset -2px -2px 0 rgba(0,0,0,0.3)",
        flexShrink: 0,
      }}
    />
  );
}
